import React, { useState } from 'react';
import { Avatar, Chip, Box, Typography, LinearProgress, Tooltip, useTheme } from '@mui/material';
import Button from 'ui/components/Button/Button';
import { useTranslation } from 'react-i18next';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { parseJsonTranslate } from 'utils/parse';
import { roleList } from 'utils/constUtils';
import { smallDate, smallTime } from 'utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';

import styles from './NextSessionLine.styles';

function LinearProgressWithLabel({ sessionValidated, sessionTotal, showTooltipProgress }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const themedStyles = styles(theme);

  const value = (sessionValidated / sessionTotal) * 100;

  const textContent = t('next_session_card.default.session', {
    count: sessionValidated,
    sessionValidated,
    sessionTotal,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      }}>
      <Tooltip title={showTooltipProgress ? textContent : ''}>
        <Box sx={{ width: showTooltipProgress ? '40px' : '90px', mr: 1 }}>
          <LinearProgress variant="determinate" sx={themedStyles.progressBar} value={value} />
        </Box>
      </Tooltip>
      {!showTooltipProgress && (
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" sx={themedStyles.progressBarText}>
            {textContent}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

const NextSessionLine = ({
  session,
  showTooltipProgress,
  onClickUnplannedButton,
  onClickShowProfile,
  showUserType = roleList.COACH,
}) => {
  const theme = useTheme();
  const themedStyles = styles(theme);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isUnplannedSession = !session.date;

  const userInfo =
    showUserType === roleList.COACH
      ? { ...(session?.program?.coach || {}), ...(session?.program?.coach?.user || {}) }
      : session.program.coachee || session.program.guest;

  const onClick = () => {
    navigate(`/program/${session.program.id}#session-${session.id}`);
  };

  const sessionTotal = session?.program?.sessions?.length;
  const sessionValidated = session?.program?.sessions?.filter(({ done }) => done).length;

  const onClickProfile = (event) => {
    if (showUserType === roleList.COACH && session?.program?.coach?.id) {
      onClickShowProfile({ type: roleList.COACH, id: session?.program?.coach?.id });
      event.stopPropagation();
    } else if (session?.program?.coachee?.id) {
      onClickShowProfile({ type: roleList.COACHEE, id: session?.program?.coachee?.id });
      event.stopPropagation();
    }
  };

  const onClickCalLink = (event) => {
    event.stopPropagation();
    window.open(session.calLink, '_blank');
  };

  return (
    <Box sx={themedStyles.container} onClick={onClick}>
      {!isUnplannedSession && (
        <Box>
          <Typography sx={themedStyles.date}>{smallDate(session.date)}</Typography>
          <Typography sx={themedStyles.time}>{smallTime(session.date)}</Typography>
        </Box>
      )}

      {isUnplannedSession && (
        <Box>
          <Typography sx={themedStyles.unplannedSession}>{t('next_session_card.default.unplanned')}</Typography>
        </Box>
      )}

      <Box sx={themedStyles.border} />

      <Box sx={themedStyles.programContainer}>
        <Typography sx={themedStyles.sessionTitle}>{parseJsonTranslate(session.name)}</Typography>
        <Typography sx={themedStyles.themeTitle}>{parseJsonTranslate(session?.program?.theme?.name)}</Typography>
      </Box>

      <Box sx={{ flex: 0.8 }}>
        <Box sx={themedStyles.userContainer} onClick={onClickProfile}>
          <Avatar src={userInfo?.picture || ''} sx={themedStyles.userAvatar} />

          <Typography sx={themedStyles.userName}>
            {t(`${userInfo?.firstname || ''} ${userInfo?.lastname || ''}`)}
          </Typography>
        </Box>
      </Box>

      {sessionTotal !== 0 && (
        <Box sx={themedStyles.progressContainer}>
          <LinearProgressWithLabel
            showTooltipProgress={showTooltipProgress}
            sessionTotal={sessionTotal}
            sessionValidated={sessionValidated}
          />
        </Box>
      )}

      {!isUnplannedSession && !session?.calLink && !!session?.duration && (
        <Box>
          <Chip sx={themedStyles.chip} label={`${session.duration} min`} variant="outlined" />
        </Box>
      )}

      {isUnplannedSession && (
        <Box sx={themedStyles.unplannedButtonContainer}>
          <Button size="xs" sx={{ height: '32px' }} onClick={onClickUnplannedButton}>
            <CalendarTodayIcon sx={themedStyles.calendarIcon} />
            {t('next_session_card.default.unplanned_button')}
          </Button>
        </Box>
      )}

      {!isUnplannedSession && session?.calLink && (
        <Box sx={themedStyles.unplannedButtonContainer}>
          <Button size="xs" sx={{ height: '32px' }} onClick={onClickCalLink}>
            <VideocamOutlinedIcon sx={themedStyles.camIcon} />
            {t('next_session_card.default.join_session')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default NextSessionLine;
